import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../ui/command";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import {
  Trash2, Loader2, Check, ChevronsUpDown, Beer, Utensils, Package, Snowflake,
  FlameKindling, Clock, Gift, Coffee,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Fase 5 — Registro rápido de merma.
 * Un modal mobile-friendly que inyecta un stock_movement tipado con motivo
 * (rotura / caducidad / regalo / staff meal). Si el ingrediente tiene formatos
 * definidos, se puede elegir una copa/botella para que el sistema convierta a
 * la unidad base automáticamente.
 */

const REASONS = [
  { value: "breakage",   label: "Rotura",      icon: FlameKindling, color: "red",    description: "Se cayó, se rompió, derrame" },
  { value: "waste",      label: "Caducidad",   icon: Clock,         color: "amber",  description: "Fuera de fecha, en mal estado" },
  { value: "comp",       label: "Regalo / Cortesía", icon: Gift,   color: "emerald", description: "Invitación al cliente, cortesía" },
  { value: "staff_meal", label: "Staff meal",  icon: Coffee,        color: "cyan",   description: "Comida del personal" },
];

const ZONES = [
  { id: "barra", label: "Barra", icon: Beer },
  { id: "cocina", label: "Cocina", icon: Utensils },
  { id: "almacen", label: "Almacén", icon: Package },
  { id: "camara", label: "Cámara", icon: Snowflake },
];

export function WasteDialog({ open, onOpenChange, restaurantId, preselectedIngredientId, onSaved }) {
  const [ingredients, setIngredients] = useState([]);
  const [ingredientId, setIngredientId] = useState(preselectedIngredientId || "");
  const [picker, setPicker] = useState(false);
  const [reason, setReason] = useState("breakage");
  const [qty, setQty] = useState("1");
  const [formatId, setFormatId] = useState(""); // for beverage ingredients
  const [zone, setZone] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const ings = await api.getIngredients(restaurantId);
      setIngredients(ings);
    } catch {
      toast.error("Error al cargar ingredientes");
    }
  }, [restaurantId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (open) {
      setIngredientId(preselectedIngredientId || "");
      setReason("breakage");
      setQty("1");
      setFormatId("");
      setZone("");
      setNote("");
    }
  }, [open, preselectedIngredientId]);

  const selected = useMemo(
    () => ingredients.find((i) => i.ingredient_id === ingredientId),
    [ingredients, ingredientId],
  );

  const availableFormats = selected?.formats || [];

  const selectedFormat = availableFormats.find((f) => f.format_id === formatId);
  const qtyInBase = useMemo(() => {
    const n = parseFloat(qty);
    if (!Number.isFinite(n)) return 0;
    if (selectedFormat) return n * Number(selectedFormat.size_in_base || 0);
    return n;
  }, [qty, selectedFormat]);

  const estimatedCost = selected ? qtyInBase * Number(selected.cost_per_unit || 0) : 0;

  const handleSave = async () => {
    if (!ingredientId) { toast.error("Selecciona un producto"); return; }
    const n = parseFloat(qty);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Cantidad inválida"); return; }
    setSaving(true);
    try {
      await api.createStockMovement(restaurantId, {
        ingredient_id: ingredientId,
        kind: reason,
        qty: -Math.abs(n), // losses are always negative
        zone: zone || null,
        note: note || null,
        format_id: selectedFormat ? selectedFormat.format_id : null,
        format_qty: selectedFormat ? -Math.abs(n) : null,
      });
      const reasonLabel = REASONS.find((r) => r.value === reason)?.label || "Merma";
      toast.success(`${reasonLabel} registrada`, {
        description: `${selected?.name || "Producto"} · Impacto: −€${estimatedCost.toFixed(2)}`,
      });
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="waste-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-destructive" /> Registrar merma
          </DialogTitle>
          <DialogDescription>
            Registra una pérdida de stock indicando el motivo. Se creará un movimiento tipado en el inventario.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          {/* Ingredient picker */}
          <div className="space-y-1.5">
            <Label>Producto</Label>
            <Popover open={picker} onOpenChange={setPicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline" role="combobox"
                  className="w-full justify-between font-normal"
                  data-testid="waste-ingredient-picker"
                >
                  <span className="truncate">
                    {selected ? `${selected.name} (${selected.unit})` : "Elige un producto"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar…" data-testid="waste-ingredient-search" />
                  <CommandList>
                    <CommandEmpty>Sin resultados</CommandEmpty>
                    <CommandGroup>
                      {ingredients.map((ing) => (
                        <CommandItem
                          key={ing.ingredient_id}
                          value={`${ing.name} ${ing.unit}`}
                          onSelect={() => {
                            setIngredientId(ing.ingredient_id);
                            setFormatId("");
                            setPicker(false);
                          }}
                          data-testid={`waste-ing-${ing.ingredient_id}`}
                        >
                          <Check className={`mr-2 h-4 w-4 ${ingredientId === ing.ingredient_id ? "opacity-100" : "opacity-0"}`} />
                          <span className="flex-1 truncate">{ing.name}</span>
                          <span className="text-xs text-muted-foreground ml-1">{ing.unit}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Reason */}
          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <div className="grid grid-cols-2 gap-2">
              {REASONS.map((r) => {
                const Icon = r.icon;
                const active = reason === r.value;
                return (
                  <button
                    key={r.value} type="button" onClick={() => setReason(r.value)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-colors ${active ? `border-${r.color}-500 bg-${r.color}-50 dark:bg-${r.color}-950/30` : "border-border hover:bg-muted"}`}
                    data-testid={`waste-reason-${r.value}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${active ? `text-${r.color}-600` : "text-muted-foreground"}`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${active ? "text-foreground" : ""}`}>{r.label}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{r.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Qty + Format */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="waste-qty">Cantidad</Label>
              <Input
                id="waste-qty" type="number" min="0" step="0.01" inputMode="decimal"
                value={qty} onChange={(e) => setQty(e.target.value)}
                data-testid="waste-qty-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Formato</Label>
              {availableFormats.length > 0 ? (
                <Select
                  value={formatId || "__base__"}
                  onValueChange={(v) => setFormatId(v === "__base__" ? "" : v)}
                >
                  <SelectTrigger data-testid="waste-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__base__">{selected?.unit || "Base"}</SelectItem>
                    {availableFormats.map((f) => (
                      <SelectItem key={f.format_id} value={f.format_id}>
                        {f.name} ({Number(f.size_in_base).toLocaleString("es-ES")} {selected?.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="h-10 px-3 border rounded-md flex items-center text-sm text-muted-foreground">
                  {selected?.unit || "—"}
                </div>
              )}
            </div>
          </div>

          {/* Zone + Note */}
          <div className="space-y-1.5">
            <Label>Zona (opcional)</Label>
            <div className="grid grid-cols-4 gap-1.5">
              {ZONES.map((z) => {
                const Icon = z.icon;
                const active = zone === z.id;
                return (
                  <button
                    key={z.id} type="button"
                    onClick={() => setZone(active ? "" : z.id)}
                    className={`flex flex-col items-center gap-0.5 py-2 rounded-md border transition-colors ${active ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
                    data-testid={`waste-zone-${z.id}`}
                  >
                    <Icon className={`w-4 h-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
                    <span className="text-[10px]">{z.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="waste-note">Nota (opcional)</Label>
            <Textarea
              id="waste-note" rows={2}
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Ej: cayó al limpiar la estantería"
              data-testid="waste-note-input"
            />
          </div>

          {selected && qtyInBase > 0 && (
            <div className="rounded-md border bg-muted/30 p-2.5 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">Impacto estimado</span>
              <Badge variant="destructive" className="tabular-nums" data-testid="waste-impact-badge">
                −€{estimatedCost.toFixed(2)} <span className="ml-1 opacity-70">({qtyInBase.toLocaleString("es-ES", { maximumFractionDigits: 3 })} {selected.unit})</span>
              </Badge>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !ingredientId} variant="destructive" data-testid="waste-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
            Registrar merma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WasteDialog;
